"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ArrowUp, FileText, Layers, Square } from "lucide-react";
import Image from "next/image";

import { cn } from "@/shared/lib/utils/cn";
import { AgentAvatar } from "@/shared/ui/agent-avatar";
import { Button } from "@/shared/ui/shadcn-components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/shadcn-components/dialog";
import {
  isEphemeralThreadTitle,
  titleFromPrompt,
  upsertInsightsThread,
} from "../history/insights-history";
import { insightsFetch } from "../transport/insights-api";
import { consumeInsightsSse } from "../transport/insights-sse";
import type {
  InsightsAssistantKind,
  InsightsChatItem,
  InsightsCoverageSnapshot,
  InsightsTaskList,
  InsightsThreadInvestigationSummary,
  InsightsToolActivity,
  InsightsUserQuestion,
  InsightsWorkingFrame,
} from "../types";
import { InsightsAnswer } from "./InsightsAnswer";
import { applyInvestigationBind } from "./apply-investigation-bind";
import { canShowInvestigationReport } from "./build-investigation-report";
import { deriveEvidenceLine } from "./derive-evidence-line";
import { groupInvestigationTasks } from "./group-investigation-tasks";
import { groupInsightsTranscript, toolActivityRowLabel } from "./group-tool-stack";
import { buildHydratedItems, stampInvestigationIdsFromSummaries } from "./hydrate-transcript";
import { InsightsInvestigationCollapse } from "./InsightsInvestigationCollapse";
import { InsightsInvestigationReportDialog } from "./InsightsInvestigationReportDialog";
import { InsightsInvestigationSidebar } from "./InsightsInvestigationSidebar";
import { InsightsLiveStatus } from "./InsightsLiveStatus";
import { InsightsTaskSection } from "./InsightsTaskSection";
import { InsightsUserQuestionCard } from "./InsightsUserQuestion";
import { ensureInsightsNotificationPermission, notifyInsightsHitl } from "./insights-hitl-notify";
import {
  coverageFromToolActivity,
  isFullCatalogCoverageDump,
  mergeCoverageSnapshots,
  parseConnectionsCoverage,
  type InsightsCoverageSnapshot as CoverageSnap,
} from "./coverage";
import {
  preferWorkingFrame,
  workingFrameFromCoveragePayload,
  workingFrameFromToolActivity,
} from "./working-frame";

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export type InsightsInvestigationPickerState = {
  investigations: InsightsThreadInvestigationSummary[];
  selectedInvestigationId: string | null;
  activeInvestigationId: string | null;
  question: string | null;
  selectInvestigation: (investigationId: string) => void;
};

export function InsightsChat({
  threadId,
  initialPrompt,
  onThreadReady,
  onInvestigationQuestion,
  onInvestigationPickerChange,
}: {
  threadId: string;
  initialPrompt?: string | null;
  onThreadReady?: (threadId: string) => void;
  /** Primary user question for the page sub-header (not Continuations). */
  onInvestigationQuestion?: (question: string | null) => void;
  /** Multi-investigation subtitle picker binding. */
  onInvestigationPickerChange?: (state: InsightsInvestigationPickerState | null) => void;
}) {
  const [items, setItems] = useState<InsightsChatItem[]>([]);
  const [composer, setComposer] = useState("");
  const [running, setRunning] = useState(false);
  const [pending, setPending] = useState<InsightsUserQuestion | null>(null);
  const [taskList, setTaskList] = useState<InsightsTaskList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [runStartedAt, setRunStartedAt] = useState<string | null>(null);
  const [runDurationMs, setRunDurationMs] = useState<number | null>(null);
  const [hostCoverage, setHostCoverage] = useState<InsightsCoverageSnapshot | null>(null);
  const [hostWorkingFrame, setHostWorkingFrame] = useState<InsightsWorkingFrame | null>(null);
  const [contextOpen, setContextOpen] = useState(false);
  const [reportMarkdown, setReportMarkdown] = useState<string>("");
  const [reportOpen, setReportOpen] = useState(false);
  const [investigations, setInvestigations] = useState<InsightsThreadInvestigationSummary[]>([]);
  const [activeInvestigationId, setActiveInvestigationId] = useState<string | null>(null);
  const [selectedInvestigationId, setSelectedInvestigationId] = useState<string | null>(null);
  /** Tasks rail click — expand + scroll to this task id in the feed. */
  const [focusTaskId, setFocusTaskId] = useState<string | null>(null);
  const [pendingScrollInvestigationId, setPendingScrollInvestigationId] = useState<string | null>(
    null,
  );
  const abortRef = useRef<AbortController | null>(null);
  /** Which SSE owns applySseEvent: /run|/hitl suppress /watch (no dual text-delta). */
  const streamOwnerRef = useRef<"idle" | "run" | "watch">("idle");
  const bootstrapped = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedInvestigationIdRef = useRef<string | null>(null);
  const activeInvestigationIdRef = useRef<string | null>(null);

  useEffect(() => {
    selectedInvestigationIdRef.current = selectedInvestigationId;
    activeInvestigationIdRef.current = activeInvestigationId;
  }, [selectedInvestigationId, activeInvestigationId]);

  const viewingHistorical =
    Boolean(selectedInvestigationId) &&
    Boolean(activeInvestigationId) &&
    selectedInvestigationId !== activeInvestigationId;
  // Historical picker view is read-only — do not paint live run/HITL chrome onto it.
  const live = (running || Boolean(pending)) && !viewingHistorical;

  useEffect(() => {
    if (!live) return;
    const el = scrollRef.current;
    if (!el) return;
    // Scope to the chat pane only — Element.scrollIntoView also scrolls outer
    // ancestors and can shove the whole Insights shell (nav + rails) upward.
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [items, pending, live]);

  // Notify when HITL arrives while this tab is in the background.
  useEffect(() => {
    if (!pending?.question || viewingHistorical) return;
    notifyInsightsHitl({
      question: pending.question,
      threadId,
      toolCallId: pending.toolCallId,
    });
  }, [pending, threadId, viewingHistorical]);

  const applySseEvent = useCallback(
    (event: string, data: unknown) => {
      const d = data as Record<string, unknown>;
      // Selected a prior investigation while the active one is still streaming —
      // ignore live transcript / rail / HITL so they do not bleed into history.
      const viewingHistoricalSpine = () => {
        const sel = selectedInvestigationIdRef.current;
        const act = activeInvestigationIdRef.current;
        return Boolean(sel && act && sel !== act);
      };
      if (
        viewingHistoricalSpine() &&
        (event === "text-delta" ||
          event === "assistant-message" ||
          event === "final-answer" ||
          event === "tool-call" ||
          event === "tool-result" ||
          event === "user-message" ||
          event === "user-question" ||
          event === "task-list" ||
          event === "coverage-snapshot" ||
          event === "working-frame" ||
          event === "report-markdown" ||
          event === "run-timing")
      ) {
        return;
      }
      if (event === "text-delta" && typeof d.text === "string") {
        const partId = typeof d.partId === "string" ? d.partId : undefined;
        const assistantKind =
          typeof d.assistantKind === "string"
            ? (d.assistantKind as InsightsAssistantKind)
            : undefined;
        // Prefer host cumulative fullText when present — idempotent under duplicate SSE.
        const nextText = typeof d.fullText === "string" ? (d.fullText as string) : null;
        setItems((prev) => {
          const last = prev[prev.length - 1];
          if (last?.kind === "assistant" && last.streaming) {
            const text = nextText ?? last.text + d.text;
            if (text === last.text) return prev;
            return [
              ...prev.slice(0, -1),
              {
                ...last,
                text,
                ...(partId ? { partId, id: partId } : {}),
                ...(assistantKind ? { assistantKind } : {}),
              },
            ];
          }
          return [
            ...prev,
            {
              id: partId || newId(),
              kind: "assistant",
              text: nextText ?? (d.text as string),
              streaming: true,
              ...(partId ? { partId } : {}),
              ...(assistantKind ? { assistantKind } : {}),
            },
          ];
        });
        return;
      }
      if (event === "assistant-message" || event === "final-answer") {
        const partId =
          (typeof d.partId === "string" && d.partId) ||
          (typeof d.id === "string" && d.id) ||
          undefined;
        const fullText = typeof d.text === "string" ? d.text : "";
        const assistantKind =
          typeof d.assistantKind === "string"
            ? (d.assistantKind as InsightsAssistantKind)
            : event === "final-answer"
              ? ("final_answer" as const)
              : undefined;
        if (!partId && !fullText) return;
        setItems((prev) => {
          const idx = partId
            ? prev.findIndex(
                (it) => it.kind === "assistant" && (it.partId === partId || it.id === partId),
              )
            : -1;
          if (idx >= 0) {
            const cur = prev[idx]!;
            if (cur.kind !== "assistant") return prev;
            const next = [...prev];
            next[idx] = {
              ...cur,
              id: partId || cur.id,
              partId: partId || cur.partId,
              text: fullText || cur.text,
              streaming: false,
              ...(assistantKind ? { assistantKind } : {}),
            };
            return next;
          }
          if (!fullText) return prev;
          return [
            ...prev.map((item) =>
              item.kind === "assistant" && item.streaming ? { ...item, streaming: false } : item,
            ),
            {
              id: partId || newId(),
              kind: "assistant" as const,
              text: fullText,
              streaming: false,
              ...(partId ? { partId } : {}),
              ...(assistantKind ? { assistantKind } : {}),
            },
          ];
        });
        return;
      }
      if (event === "tool-call") {
        const activity: InsightsToolActivity = {
          id: (d.eventId as string) || newId(),
          tool: String(d.tool ?? "tool"),
          phase: "call",
          args: d.args,
          ...(typeof d.taskId === "string" ? { taskId: d.taskId } : {}),
          ...(typeof d.toolCallId === "string" ? { toolCallId: d.toolCallId } : {}),
          ...(typeof d.startedAt === "string" ? { startedAt: d.startedAt } : {}),
        };
        setItems((prev) => {
          const closed = prev.map((item) =>
            item.kind === "assistant" && item.streaming ? { ...item, streaming: false } : item,
          );
          return [...closed, { id: activity.id, kind: "tool", activity }];
        });
        return;
      }
      if (event === "tool-result") {
        const activity: InsightsToolActivity = {
          id: (d.eventId as string) || newId(),
          tool: String(d.tool ?? "tool"),
          phase: "result",
          result: d.result,
          error: Boolean(d.error),
          softRefuse: Boolean(d.softRefuse),
          ...(typeof d.taskId === "string" ? { taskId: d.taskId } : {}),
          ...(typeof d.toolCallId === "string" ? { toolCallId: d.toolCallId } : {}),
          ...(typeof d.startedAt === "string" ? { startedAt: d.startedAt } : {}),
          ...(typeof d.endedAt === "string" ? { endedAt: d.endedAt } : {}),
          ...(typeof d.durationMs === "number" ? { durationMs: d.durationMs } : {}),
        };
        setItems((prev) => [...prev, { id: activity.id, kind: "tool", activity }]);
        return;
      }
      if (event === "task-list") {
        if (
          selectedInvestigationIdRef.current &&
          activeInvestigationIdRef.current &&
          selectedInvestigationIdRef.current !== activeInvestigationIdRef.current
        ) {
          return;
        }
        const list = data as InsightsTaskList;
        setTaskList(Array.isArray(list?.tasks) && list.tasks.length > 0 ? list : null);
        return;
      }
      if (event === "run-timing") {
        if (typeof d.startedAt === "string") setRunStartedAt(d.startedAt);
        if (typeof d.durationMs === "number") {
          setRunDurationMs(d.durationMs);
        } else if (d.durationMs === null) {
          setRunDurationMs(null);
        }
        return;
      }
      if (event === "investigation-bind" && d && typeof d === "object") {
        const bound = applyInvestigationBind(d as Parameters<typeof applyInvestigationBind>[0]);
        if (!bound) return;
        // Fork while browsing a prior inv: refresh picker + active id only.
        if (viewingHistoricalSpine()) {
          setInvestigations(bound.investigations);
          setActiveInvestigationId(bound.activeInvestigationId);
          return;
        }
        setInvestigations(bound.investigations);
        setActiveInvestigationId(bound.activeInvestigationId);
        setSelectedInvestigationId(bound.selectedInvestigationId);
        setItems(bound.items);
        setTaskList(bound.taskList);
        setHostCoverage(bound.hostCoverage);
        setHostWorkingFrame(bound.hostWorkingFrame);
        setReportMarkdown(bound.reportMarkdown);
        if (bound.runStartedAt) setRunStartedAt(bound.runStartedAt);
        setRunDurationMs(bound.runDurationMs);
        if (bound.primaryQuestion) {
          onInvestigationQuestion?.(bound.primaryQuestion);
          upsertInsightsThread({
            threadId,
            title: titleFromPrompt(bound.primaryQuestion),
          });
        }
        return;
      }
      if (event === "user-message" && d && typeof d === "object") {
        const content = typeof d.content === "string" ? d.content : "";
        const invId = typeof d.investigationId === "string" ? d.investigationId : undefined;
        const serverId = typeof d.id === "string" ? d.id : undefined;
        if (content) {
          setItems((prev) => {
            // Stamp / adopt server id on the latest matching optimistic bubble only.
            for (let i = prev.length - 1; i >= 0; i -= 1) {
              const row = prev[i];
              if (!row || row.kind !== "user" || row.text !== content) continue;
              if ((!invId || row.investigationId === invId) && (!serverId || row.id === serverId)) {
                return prev;
              }
              const next = prev.slice();
              next[i] = {
                ...row,
                ...(invId ? { investigationId: invId } : {}),
                ...(serverId ? { id: serverId } : {}),
              };
              return next;
            }
            return prev;
          });
        }
        return;
      }
      if (event === "coverage-snapshot" && d && typeof d === "object") {
        if (
          selectedInvestigationIdRef.current &&
          activeInvestigationIdRef.current &&
          selectedInvestigationIdRef.current !== activeInvestigationIdRef.current
        ) {
          return;
        }
        const platforms = Array.isArray((d as CoverageSnap).platforms)
          ? (d as CoverageSnap).platforms
          : null;
        if (platforms) {
          if (platforms.length === 0) {
            setHostCoverage(null);
          } else {
            setHostCoverage({
              platforms,
              updatedAt:
                typeof (d as CoverageSnap).updatedAt === "string"
                  ? (d as CoverageSnap).updatedAt
                  : new Date().toISOString(),
            });
          }
        }
        return;
      }
      if (event === "working-frame" && d && typeof d === "object") {
        if (
          selectedInvestigationIdRef.current &&
          activeInvestigationIdRef.current &&
          selectedInvestigationIdRef.current !== activeInvestigationIdRef.current
        ) {
          return;
        }
        if ((d as { cleared?: unknown }).cleared === true) {
          setHostWorkingFrame(null);
          return;
        }
        const frame = workingFrameFromCoveragePayload(d);
        if (frame?.definition) setHostWorkingFrame(frame);
        return;
      }
      if (event === "report-markdown" && d && typeof d === "object") {
        if (
          selectedInvestigationIdRef.current &&
          activeInvestigationIdRef.current &&
          selectedInvestigationIdRef.current !== activeInvestigationIdRef.current
        ) {
          return;
        }
        const md = (d as { markdown?: unknown }).markdown;
        if (typeof md === "string") {
          setReportMarkdown(md.trim() ? md : "");
        }
        return;
      }
      if (event === "user-question") {
        setPending(data as InsightsUserQuestion);
        setItems((prev) =>
          prev.map((item) =>
            item.kind === "assistant" && item.streaming ? { ...item, streaming: false } : item,
          ),
        );
        return;
      }
      if (event === "done" || event === "agent-stopped") {
        setRunning(false);
        if (!viewingHistoricalSpine()) {
          setItems((prev) =>
            prev.map((item) =>
              item.kind === "assistant" && item.streaming ? { ...item, streaming: false } : item,
            ),
          );
          if (typeof d.durationMs === "number") {
            setRunDurationMs(d.durationMs);
          } else if (typeof d.startedAt === "string" && typeof d.endedAt === "string") {
            const start = Date.parse(d.startedAt);
            const end = Date.parse(d.endedAt);
            if (Number.isFinite(start) && Number.isFinite(end)) {
              setRunDurationMs(Math.max(0, end - start));
            }
          }
        }
        // Pull latest host-assembled brief + coverage from the session snapshot.
        void insightsFetch(`/insights/thread/session?threadId=${encodeURIComponent(threadId)}`)
          .then(async (res) => {
            if (!res.ok) return;
            const snap = (await res.json()) as {
              reportMarkdown?: string | null;
              coverageSnapshot?: InsightsCoverageSnapshot | null;
              workingFrame?: InsightsWorkingFrame | null;
              investigations?: InsightsThreadInvestigationSummary[];
              activeInvestigationId?: string | null;
              selectedInvestigationId?: string | null;
            };
            if (Array.isArray(snap.investigations)) setInvestigations(snap.investigations);
            if (snap.activeInvestigationId) {
              setActiveInvestigationId(snap.activeInvestigationId);
              // Stay on active after a live run completes (unless viewing historical).
              if (
                !selectedInvestigationIdRef.current ||
                selectedInvestigationIdRef.current === activeInvestigationIdRef.current
              ) {
                setSelectedInvestigationId(
                  snap.selectedInvestigationId ?? snap.activeInvestigationId,
                );
              }
            }
            const doneTitle =
              snap.investigations
                ?.find(
                  (i) =>
                    i.investigationId ===
                      (snap.activeInvestigationId ?? snap.selectedInvestigationId) &&
                    i.question?.trim(),
                )
                ?.question?.trim() ||
              snap.investigations?.find((i) => i.question?.trim())?.question?.trim() ||
              "";
            if (doneTitle) {
              upsertInsightsThread({ threadId, title: titleFromPrompt(doneTitle) });
            }
            if (
              selectedInvestigationIdRef.current &&
              activeInvestigationIdRef.current &&
              selectedInvestigationIdRef.current !== activeInvestigationIdRef.current
            ) {
              return;
            }
            if (typeof snap.reportMarkdown === "string" && snap.reportMarkdown.trim()) {
              setReportMarkdown(snap.reportMarkdown);
            }
            if (snap.coverageSnapshot?.platforms?.length) {
              setHostCoverage(snap.coverageSnapshot);
            }
            if (snap.workingFrame?.definition) {
              setHostWorkingFrame(snap.workingFrame);
            }
          })
          .catch(() => {
            /* ignore — host may still push report-markdown */
          });
        return;
      }
      if (event === "error") {
        setError(typeof d.message === "string" ? d.message : "Stream error");
      }
    },
    [threadId, onInvestigationQuestion],
  );

  const attachWatch = useCallback(
    async (afterSeq = 0) => {
      // Never stack watch on top of an in-flight /run or /hitl SSE — duplicate
      // text-delta delivery triples Working notes while streaming.
      if (streamOwnerRef.current === "run") return;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      streamOwnerRef.current = "watch";
      setRunning(true);
      try {
        const qs = new URLSearchParams({
          threadId,
          after: String(afterSeq),
        });
        const res = await insightsFetch(`/insights/thread/watch?${qs.toString()}`, {
          method: "POST",
          body: JSON.stringify({ threadId }),
          signal: controller.signal,
        });
        if (!res.ok) {
          const body = await res.text();
          throw new Error(body || `Watch failed (${res.status})`);
        }
        await consumeInsightsSse(res, applySseEvent, controller.signal);
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setError(err instanceof Error ? err.message : "Failed to reattach stream");
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          streamOwnerRef.current = "idle";
        }
        setRunning(false);
        setItems((prev) =>
          prev.map((item) =>
            item.kind === "assistant" && item.streaming ? { ...item, streaming: false } : item,
          ),
        );
      }
    },
    [applySseEvent, threadId],
  );

  const runMessage = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!trimmed || running) return;

      // Best chance to unlock notifications (user gesture from Send).
      void ensureInsightsNotificationPermission();

      setError(null);
      setPending(null);
      setRunning(true);
      // Keep prior chat + rail until the agent forks (investigation-bind).
      // Follow-ups may stay on the same investigation — do not blank on send.
      // Mirror HITL: keep the original investigation wall clock across Continue /
      // mid-run follow-ups. Host emits run-timing with the preserved startedAt.
      if (!runStartedAt) setRunStartedAt(new Date().toISOString());
      setRunDurationMs(null);
      setItems((prev) => [...prev, { id: newId(), kind: "user", text: trimmed }]);
      // Sidebar is localStorage-only; do not wait for investigation SSE (can be
      // missed on /watch reconnect). Ephemeral nudges won't overwrite a real title.
      upsertInsightsThread({ threadId, title: titleFromPrompt(trimmed) });
      onThreadReady?.(threadId);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      streamOwnerRef.current = "run";

      try {
        const res = await insightsFetch("/insights/thread/run", {
          method: "POST",
          body: JSON.stringify({ threadId, message: trimmed }),
          signal: controller.signal,
        });
        if (!res.ok) {
          const body = await res.text();
          throw new Error(body || `Run failed (${res.status})`);
        }
        await consumeInsightsSse(res, applySseEvent, controller.signal);
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setError(err instanceof Error ? err.message : "Failed to run insights");
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          streamOwnerRef.current = "idle";
        }
        setRunning(false);
        setItems((prev) =>
          prev.map((item) =>
            item.kind === "assistant" && item.streaming ? { ...item, streaming: false } : item,
          ),
        );
      }
    },
    [applySseEvent, onThreadReady, runStartedAt, running, threadId],
  );

  const submitHitl = useCallback(
    async (answer: string | string[]) => {
      if (!pending || running) return;
      void ensureInsightsNotificationPermission();
      setRunning(true);
      setError(null);
      if (!runStartedAt) setRunStartedAt(new Date().toISOString());
      const display = Array.isArray(answer) ? answer.join(", ") : answer;
      setItems((prev) => [...prev, { id: newId(), kind: "user", text: display }]);
      const question = pending;
      setPending(null);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      streamOwnerRef.current = "run";
      try {
        const res = await insightsFetch("/insights/thread/hitl", {
          method: "POST",
          body: JSON.stringify({
            threadId,
            toolCallId: question.toolCallId,
            runId: question.runId,
            answers: answer,
          }),
          signal: controller.signal,
        });
        if (!res.ok) {
          const body = await res.text();
          throw new Error(body || `HITL failed (${res.status})`);
        }
        await consumeInsightsSse(res, applySseEvent, controller.signal);
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setError(err instanceof Error ? err.message : "Failed to resume");
          setPending(question);
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          streamOwnerRef.current = "idle";
        }
        setRunning(false);
      }
    },
    [applySseEvent, pending, runStartedAt, running, threadId],
  );

  const stop = useCallback(async () => {
    abortRef.current?.abort();
    streamOwnerRef.current = "idle";
    try {
      await insightsFetch("/insights/thread/stop", {
        method: "POST",
        body: JSON.stringify({ threadId }),
      });
    } catch {
      // ignore
    }
    setRunning(false);
  }, [threadId]);

  // Hydrate existing session + reattach live run via /watch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await insightsFetch(
          `/insights/thread/session?threadId=${encodeURIComponent(threadId)}`,
        );
        if (!res.ok || cancelled) return;
        const snap = (await res.json()) as {
          messages?: Array<{
            id?: string;
            role: string;
            content: string;
            createdAt?: string;
            investigationId?: string;
          }>;
          pendingQuestion?: InsightsUserQuestion | null;
          taskList?: InsightsTaskList | null;
          toolActivity?: InsightsToolActivity[];
          streamingAssistantText?: string | null;
          events?: Array<{ event: string; data: unknown; seq?: number }>;
          runStartedAt?: string | null;
          runEndedAt?: string | null;
          runDurationMs?: number | null;
          runActive?: boolean;
          lastSeq?: number;
          coverageSnapshot?: InsightsCoverageSnapshot | null;
          workingFrame?: InsightsWorkingFrame | null;
          reportMarkdown?: string | null;
          investigations?: InsightsThreadInvestigationSummary[];
          activeInvestigationId?: string | null;
          selectedInvestigationId?: string | null;
          investigationPrimaryQuestion?: string | null;
        };
        if (cancelled) return;

        if (Array.isArray(snap.investigations)) {
          setInvestigations(snap.investigations);
        }
        const activeId = snap.activeInvestigationId ?? snap.selectedInvestigationId ?? null;
        if (activeId) {
          setActiveInvestigationId(activeId);
          setSelectedInvestigationId(snap.selectedInvestigationId ?? activeId);
        }
        // Ensure this thread appears in the sidebar after refresh / mid-run attach
        // (investigation SSE is often already past lastSeq and never upserts).
        const hydrateTitle =
          snap.investigationPrimaryQuestion?.trim() ||
          snap.investigations?.find((i) => i.question?.trim())?.question?.trim() ||
          snap.messages?.find((m) => m.role === "user" && m.content?.trim())?.content?.trim() ||
          "";
        if (hydrateTitle) {
          upsertInsightsThread({ threadId, title: titleFromPrompt(hydrateTitle) });
        } else {
          upsertInsightsThread({ threadId, title: "New investigation" });
        }
        if (snap.taskList?.tasks?.length) setTaskList(snap.taskList);
        else setTaskList(null);
        if (snap.pendingQuestion) setPending(snap.pendingQuestion);
        if (snap.runStartedAt) setRunStartedAt(snap.runStartedAt);
        if (typeof snap.runDurationMs === "number") setRunDurationMs(snap.runDurationMs);
        if (snap.coverageSnapshot?.platforms?.length) {
          setHostCoverage(snap.coverageSnapshot);
        } else {
          setHostCoverage(null);
        }
        if (snap.workingFrame?.definition) {
          setHostWorkingFrame(snap.workingFrame);
        } else {
          setHostWorkingFrame(null);
        }
        if (typeof snap.reportMarkdown === "string" && snap.reportMarkdown.trim()) {
          setReportMarkdown(snap.reportMarkdown);
        } else {
          setReportMarkdown("");
        }

        // Restore coverage / frame from event buffer when snapshot field missing
        if (snap.events?.length) {
          for (const ev of snap.events) {
            if (ev.event === "coverage-snapshot") {
              const cov = ev.data as CoverageSnap;
              if (cov?.platforms?.length) setHostCoverage(cov);
            }
            if (ev.event === "working-frame") {
              const frame = workingFrameFromCoveragePayload(ev.data);
              if (frame?.definition) setHostWorkingFrame(frame);
            }
          }
        }

        const nextItems = stampInvestigationIdsFromSummaries(
          buildHydratedItems(
            snap.messages ?? [],
            snap.toolActivity ?? [],
            snap.streamingAssistantText,
            snap.events,
          ),
          snap.investigations ?? [],
        );
        // Don't clobber a live /run transcript with a mid-flight session hydrate.
        if (nextItems.length && streamOwnerRef.current !== "run") {
          setItems(nextItems);
        }

        if (snap.runActive && streamOwnerRef.current !== "run") {
          void attachWatch(snap.lastSeq ?? 0);
        }
      } catch {
        // fresh thread
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachWatch, threadId]);

  // Bootstrap from home prompt (skip if session already has content)
  useEffect(() => {
    if (bootstrapped.current) return;
    if (!initialPrompt?.trim()) return;
    const timer = window.setTimeout(() => {
      if (bootstrapped.current) return;
      if (items.length > 0 || running) {
        bootstrapped.current = true;
        return;
      }
      bootstrapped.current = true;
      void runMessage(initialPrompt.trim());
    }, 150);
    return () => window.clearTimeout(timer);
  }, [initialPrompt, items.length, runMessage, running]);

  const transcriptRows = useMemo(() => groupInsightsTranscript(items, live), [items, live]);

  // Session items are already investigation-scoped (rail parity). No client slice.
  const viewRows = useMemo(
    () =>
      groupInvestigationTasks(transcriptRows, {
        running: live,
        taskList,
      }),
    [transcriptRows, live, taskList],
  );

  const currentToolLabel = useMemo(() => {
    for (let i = transcriptRows.length - 1; i >= 0; i -= 1) {
      const row = transcriptRows[i]!;
      if (row.type !== "tool-stack") continue;
      const pendingTool = [...row.activities].reverse().find((a) => a.pending);
      if (pendingTool) return toolActivityRowLabel(pendingTool);
      const last = row.activities.at(-1);
      if (last && row.live) return toolActivityRowLabel(last);
    }
    return null;
  }, [transcriptRows]);

  // Rebuild coverage/frame from tools after the latest user turn so prior-question
  // strip/frame do not bleed into a follow-up before the host writes again.
  const turnToolItems = useMemo(() => {
    let lastUser = -1;
    for (let i = items.length - 1; i >= 0; i -= 1) {
      if (items[i]?.kind === "user") {
        lastUser = i;
        break;
      }
    }
    return lastUser >= 0 ? items.slice(lastUser + 1) : items;
  }, [items]);

  const parsedCoverage = useMemo(() => coverageFromToolActivity(turnToolItems), [turnToolItems]);
  const coverage = useMemo(() => {
    // Prefer host SSE/hydrate; fall back to tool-activity rebuild (never ledger).
    // Drop accidental full-catalog dumps (selected estate only).
    // Historical selection: host spine only — do not merge latest-turn tools.
    if (viewingHistorical) {
      return hostCoverage ?? { platforms: [] as CoverageSnap["platforms"], updatedAt: undefined };
    }
    let catalog: CoverageSnap["platforms"] = [];
    for (const item of items) {
      if (item.kind !== "tool" || !item.activity) continue;
      if (item.activity.phase === "result" && item.activity.tool === "list_connections") {
        catalog = parseConnectionsCoverage(item.activity.result);
      }
    }
    const scrub = (snap: CoverageSnap | null | undefined): CoverageSnap | null => {
      if (!snap?.platforms.length) return null;
      if (catalog.length && isFullCatalogCoverageDump(snap.platforms, catalog)) return null;
      return snap;
    };
    const host = scrub(hostCoverage);
    const parsed = scrub(parsedCoverage);
    if (host?.platforms.length) {
      if (!parsed?.platforms.length) return host;
      return mergeCoverageSnapshots(host, parsed);
    }
    return parsed ?? { platforms: [] as CoverageSnap["platforms"], updatedAt: undefined };
  }, [hostCoverage, parsedCoverage, items, viewingHistorical]);

  const coverageOrNull = coverage.platforms.length ? coverage : null;
  const hasCoverageBlockers = Boolean(coverageOrNull?.platforms.some((p) => p.needsFix));

  const parsedWorkingFrame = useMemo(
    () => workingFrameFromToolActivity(turnToolItems),
    [turnToolItems],
  );
  // Host SSE/HITL and tool-activity can each advance; take the newer stamp.
  // Historical selection: frozen host spine only.
  const workingFrame = viewingHistorical
    ? hostWorkingFrame
    : preferWorkingFrame(hostWorkingFrame, parsedWorkingFrame);
  const showContextChrome = Boolean(coverageOrNull || workingFrame?.definition);

  const evidenceLine = useMemo(
    () => (!live ? deriveEvidenceLine(transcriptRows) : null),
    [transcriptRows, live],
  );

  const showReport = canShowInvestigationReport({
    running: running && !viewingHistorical,
    pending: Boolean(pending) && !viewingHistorical,
    items,
    taskList,
    runDurationMs,
    reportMarkdown,
  });

  const dialogMarkdown = reportMarkdown.trim();

  const selectInvestigation = useCallback(
    async (investigationId: string) => {
      const id = investigationId.trim();
      if (!id || id === selectedInvestigationIdRef.current) return;
      setSelectedInvestigationId(id);
      try {
        const qs = new URLSearchParams({
          threadId,
          investigationId: id,
        });
        const res = await insightsFetch(`/insights/thread/session?${qs.toString()}`);
        if (!res.ok) return;
        const snap = (await res.json()) as {
          messages?: Array<{
            id?: string;
            role: string;
            content: string;
            createdAt?: string;
            investigationId?: string;
            partId?: string;
            assistantKind?: InsightsAssistantKind;
          }>;
          toolActivity?: InsightsToolActivity[];
          taskList?: InsightsTaskList | null;
          coverageSnapshot?: InsightsCoverageSnapshot | null;
          workingFrame?: InsightsWorkingFrame | null;
          reportMarkdown?: string | null;
          investigations?: InsightsThreadInvestigationSummary[];
          activeInvestigationId?: string | null;
          selectedInvestigationId?: string | null;
          investigationPrimaryQuestion?: string | null;
        };
        if (Array.isArray(snap.investigations)) setInvestigations(snap.investigations);
        if (snap.activeInvestigationId) setActiveInvestigationId(snap.activeInvestigationId);
        setSelectedInvestigationId(snap.selectedInvestigationId ?? id);
        setTaskList(snap.taskList?.tasks?.length ? snap.taskList : null);
        setHostCoverage(snap.coverageSnapshot?.platforms?.length ? snap.coverageSnapshot : null);
        setHostWorkingFrame(snap.workingFrame?.definition ? snap.workingFrame : null);
        setReportMarkdown(
          typeof snap.reportMarkdown === "string" && snap.reportMarkdown.trim()
            ? snap.reportMarkdown
            : "",
        );
        setItems(
          stampInvestigationIdsFromSummaries(
            buildHydratedItems(snap.messages ?? [], snap.toolActivity ?? [], null, undefined),
            snap.investigations ?? [],
          ),
        );
        const q =
          snap.investigationPrimaryQuestion?.trim() ||
          snap.investigations?.find((i) => i.investigationId === id)?.question?.trim() ||
          null;
        onInvestigationQuestion?.(q);
        setFocusTaskId(null);
        setPendingScrollInvestigationId(id);
      } catch {
        /* ignore */
      }
    },
    [threadId, onInvestigationQuestion],
  );

  const investigationQuestion = useMemo(() => {
    // Prefer the user bubble that opened the selected investigation.
    if (selectedInvestigationId) {
      const byId = items.find(
        (item) =>
          item.kind === "user" &&
          item.investigationId === selectedInvestigationId &&
          !isEphemeralThreadTitle(item.text),
      );
      if (byId?.kind === "user") return byId;
      const fromList = investigations.find((i) => i.investigationId === selectedInvestigationId);
      if (fromList?.question.trim()) {
        return {
          id: `inv-q-${fromList.investigationId}`,
          kind: "user" as const,
          text: fromList.question,
          investigationId: fromList.investigationId,
        };
      }
      // Selected id known but opener not stamped yet — do not fall back to a
      // prior investigation's user bubble (causes header flicker after fork).
      return null;
    }
    let first: (typeof items)[number] | null = null;
    for (const item of items) {
      if (item.kind !== "user") continue;
      if (!first) first = item;
      if (!isEphemeralThreadTitle(item.text)) return item;
    }
    return first;
  }, [items, selectedInvestigationId, investigations]);

  useEffect(() => {
    const text = investigationQuestion?.text?.trim() || null;
    if (text) {
      onInvestigationQuestion?.(text);
      return;
    }
    // Selected investigation without a resolved opener yet — keep the header
    // set by the investigation SSE (do not fall back to the thread opener).
    if (selectedInvestigationId) return;
    onInvestigationQuestion?.(initialPrompt?.trim() || null);
  }, [investigationQuestion, initialPrompt, onInvestigationQuestion, selectedInvestigationId]);

  useEffect(() => {
    if (!onInvestigationPickerChange) return;
    onInvestigationPickerChange({
      investigations,
      selectedInvestigationId,
      activeInvestigationId,
      question: investigationQuestion?.text?.trim() || null,
      selectInvestigation,
    });
  }, [
    onInvestigationPickerChange,
    investigations,
    selectedInvestigationId,
    activeInvestigationId,
    investigationQuestion,
    selectInvestigation,
  ]);

  const scrollChatToSelector = useCallback((selector: string) => {
    const container = scrollRef.current;
    if (!container) return false;
    const nodes = container.querySelectorAll(selector);
    const el = nodes[nodes.length - 1] as HTMLElement | undefined;
    if (!el) return false;
    const top =
      el.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      8;
    container.scrollTo({ top, behavior: "smooth" });
    return true;
  }, []);

  const scrollToTask = useCallback((taskId: string) => {
    const id = taskId.trim();
    if (!id) return;
    setFocusTaskId(id);
  }, []);

  // After focusTaskId opens collapse + task section, scroll to it in the feed.
  useEffect(() => {
    if (!focusTaskId) return;
    const id = focusTaskId;
    const tryScroll = () => scrollChatToSelector(`[data-task-id="${CSS.escape(id)}"]`);
    const t0 = window.setTimeout(() => {
      if (tryScroll()) return;
      requestAnimationFrame(() => {
        if (tryScroll()) return;
        requestAnimationFrame(() => {
          tryScroll();
        });
      });
    }, 0);
    return () => window.clearTimeout(t0);
  }, [focusTaskId, viewRows, live, scrollChatToSelector]);

  useEffect(() => {
    if (!pendingScrollInvestigationId) return;
    const id = pendingScrollInvestigationId;
    const tryScroll = () => scrollChatToSelector(`[data-investigation-id="${CSS.escape(id)}"]`);
    const t0 = window.setTimeout(() => {
      if (tryScroll()) {
        setPendingScrollInvestigationId(null);
        return;
      }
      requestAnimationFrame(() => {
        if (tryScroll()) {
          setPendingScrollInvestigationId(null);
          return;
        }
        requestAnimationFrame(() => {
          tryScroll();
          setPendingScrollInvestigationId(null);
        });
      });
    }, 0);
    return () => window.clearTimeout(t0);
  }, [pendingScrollInvestigationId, viewRows, scrollChatToSelector]);

  // Collapse investigation trail after final answer
  const renderedBody = useMemo(() => {
    const nodes: ReactNode[] = [];
    let pendingTasks: Array<Extract<(typeof viewRows)[number], { type: "task" }>> = [];

    const flushTasks = (collapse: boolean, key: string) => {
      if (!pendingTasks.length) return;
      const toolCount = pendingTasks.reduce((n, p) => n + p.section.toolCount, 0);
      const noteCount = pendingTasks.reduce((n, p) => n + p.section.noteCount, 0);
      const focusInBatch = Boolean(
        focusTaskId && pendingTasks.some((p) => p.section.taskId === focusTaskId),
      );
      const sections = pendingTasks.map((row) => (
        <InsightsTaskSection
          key={row.section.id}
          section={row.section}
          forceExpanded={live || focusTaskId === row.section.taskId}
        />
      ));
      pendingTasks = [];
      if (collapse && !live) {
        nodes.push(
          <InsightsInvestigationCollapse
            key={key}
            toolCount={toolCount}
            noteCount={noteCount}
            defaultOpen={false}
            forceOpen={focusInBatch}
          >
            {sections}
          </InsightsInvestigationCollapse>,
        );
      } else {
        nodes.push(...sections);
      }
    };

    for (const row of viewRows) {
      if (row.type === "user") {
        flushTasks(false, `pre-user-${row.id}`);
        // Selected investigation opener lives in the page sub-header — don't duplicate.
        // Keep a scroll anchor so dropdown selection can jump here.
        const invId =
          "investigationId" in row && typeof row.investigationId === "string"
            ? row.investigationId
            : undefined;
        // Only hide the header opener — follow-ups on the same investigation stay visible.
        if (investigationQuestion && row.id === investigationQuestion.id) {
          if (invId) {
            nodes.push(
              <div
                key={`anchor-${row.id}`}
                data-investigation-id={invId}
                className="h-0 scroll-mt-3"
                aria-hidden
              />,
            );
          }
          continue;
        }
        nodes.push(
          <div key={row.id} className="flex w-full min-w-0 justify-end scroll-mt-3">
            <div
              {...(invId ? { "data-investigation-id": invId } : {})}
              className="overlay-chat-user-bubble max-w-[85%]"
            >
              <p className="whitespace-pre-wrap">{row.text}</p>
            </div>
          </div>,
        );
        continue;
      }
      if (row.type === "task") {
        pendingTasks.push(row);
        continue;
      }
      flushTasks(true, `inv-${row.id}`);
      nodes.push(
        <div
          key={row.id}
          className="w-full min-w-0 overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm"
        >
          <InsightsAnswer text={row.text} streaming={row.streaming} evidenceLine={evidenceLine} />
        </div>,
      );
    }
    flushTasks(false, "trailing-tasks");
    return nodes;
  }, [viewRows, live, investigationQuestion, evidenceLine, focusTaskId]);

  return (
    <div className="flex h-full min-h-0 gap-4">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {showContextChrome ? (
          <div className="mb-1 flex items-center px-1 lg:hidden">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 px-2 text-[12px] text-muted-foreground"
              onClick={() => setContextOpen(true)}
            >
              <Layers className="size-3.5" />
              Coverage &amp; frame
            </Button>
          </div>
        ) : null}
        <div ref={scrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto px-1 py-2">
          {items.length === 0 && !running ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Ask an analytical question to start an investigation.
            </div>
          ) : null}
          {renderedBody}
          {pending && !viewingHistorical ? (
            <div className="flex w-full min-w-0 items-start gap-3">
              <AgentAvatar />
              <div className="min-w-0 max-w-lg flex-1">
                <InsightsUserQuestionCard
                  pending={pending}
                  disabled={running}
                  onSubmit={(answer) => void submitHitl(answer)}
                />
              </div>
            </div>
          ) : null}
          {runStartedAt || runDurationMs != null || showReport ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {runStartedAt || runDurationMs != null ? (
                <InsightsLiveStatus
                  running={live}
                  startedAt={runStartedAt}
                  durationMs={pending ? null : runDurationMs}
                  currentTool={currentToolLabel}
                />
              ) : null}
              {showReport ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 px-2 text-[12px] text-muted-foreground"
                  onClick={() => setReportOpen(true)}
                >
                  <FileText className="size-3.5" />
                  View report
                </Button>
              ) : null}
            </div>
          ) : null}
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <InsightsInvestigationReportDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          markdown={dialogMarkdown}
        />

        <Dialog open={contextOpen} onOpenChange={setContextOpen}>
          <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Coverage &amp; frame</DialogTitle>
            </DialogHeader>
            <InsightsInvestigationSidebar
              coverage={coverageOrNull}
              workingFrame={workingFrame}
              taskList={null}
              showTasks={false}
            />
          </DialogContent>
        </Dialog>

        <form
          className="mt-2 shrink-0"
          onSubmit={(e) => {
            e.preventDefault();
            const value = composer;
            setComposer("");
            void runMessage(value);
          }}
        >
          <div className="overlay-composer-bar flex h-12 items-center gap-2 rounded-full px-2">
            <span className="overlay-composer-brand flex size-8 shrink-0 items-center justify-center rounded-full">
              <Image src="/figma-assets/aunix-spark.svg" alt="" width={14} height={14} />
            </span>
            <input
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              placeholder={
                pending && !viewingHistorical
                  ? "Answer the question above to continue…"
                  : "Message Aunix…"
              }
              disabled={Boolean(pending) && !viewingHistorical}
              className="h-auto min-w-0 flex-1 border-0 bg-transparent px-1 text-[13px] outline-none disabled:opacity-60"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  const value = composer;
                  setComposer("");
                  void runMessage(value);
                }
              }}
            />
            {running ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => void stop()}
                className="rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                aria-label="Stop"
              >
                <Square className="size-3.5 fill-current" />
              </Button>
            ) : (
              <Button
                type="submit"
                variant="ghost"
                size="icon-sm"
                disabled={!composer.trim() || (Boolean(pending) && !viewingHistorical)}
                className={cn(
                  "rounded-full text-white disabled:opacity-100",
                  composer.trim() && !(pending && !viewingHistorical)
                    ? "bg-primary hover:bg-primary/90 hover:text-white"
                    : "overlay-composer-send--idle hover:text-white",
                )}
                aria-label="Send"
              >
                <ArrowUp className="size-4" strokeWidth={2.25} />
              </Button>
            )}
          </div>
        </form>
      </div>

      <div className="hidden w-96 shrink-0 lg:block">
        <InsightsInvestigationSidebar
          coverage={coverageOrNull}
          workingFrame={workingFrame}
          taskList={taskList}
          hasCoverageBlockers={hasCoverageBlockers}
          onTaskClick={scrollToTask}
          runLive={live}
        />
      </div>
    </div>
  );
}
